import { useState } from 'react'
import { Check, ExternalLink, Plus, Zap } from 'lucide-react'
import NewTaskModal from '@/features/tasks/NewTaskModal'
import IndexToolbar from '@/components/IndexToolbar'
import { matchesQuery } from '@/lib/matchesQuery'
import Card from '@/components/layout/Card'
import { useCompleteTask, useRunFollowUp, useTasks } from '@/features/tasks/useTasks'
import { useAuthStore } from '@/features/auth/authStore'
import { formatShortDate, formatTaskDue } from '@/lib/dealFormat'
import { GROUP_ORDER, GROUP_TITLE, groupOf, type TaskGroup } from '@/features/tasks/taskGroups'
import { cn } from '@/lib/cn'
import type { Task } from '@/api/tasks'

/**
 * The Tasks tab of Focus — formerly its own "Needs attention" page. Tasks support Focus rather than
 * drive it: a deal can need attention with or without one, so they sit beside the ranked deals.
 */
export default function FocusTasks({
  ownerId,
  group = null,
  onShowAll,
}: {
  ownerId: string | null
  /** Only this group (a Focus tile was clicked); null shows every group. */
  group?: TaskGroup | null
  onShowAll?: () => void
}) {
  const [view, setView] = useState<'open' | 'done'>('open')
  const [adding, setAdding] = useState(false)
  const { data, isLoading } = useTasks(view)
  const complete = useCompleteTask()
  const run = useRunFollowUp()
  // The scan has agency-wide side effects (tasks for any agent, automation sends), so it's admin-only
  // on the backend — hide the trigger for agents rather than let them hit a 403.
  const isAdmin = useAuthStore(
    (s) => s.user?.role === 'AGENCY_ADMIN' || s.user?.role === 'PLATFORM_ADMIN',
  )
  const [query, setQuery] = useState('')
  const mine = (data ?? []).filter((t) => !ownerId || t.assigneeUserId === ownerId)
  const tasks = mine.filter((t) => matchesQuery(query, t.title, t.dealTitle, t.contactName, t.description))

  const groups = new Map<TaskGroup, Task[]>()
  if (view === 'open') {
    for (const t of tasks) {
      const g = groupOf(t)
      if (group && g !== group) continue
      groups.set(g, [...(groups.get(g) ?? []), t])
    }
  }
  const narrowed = view === 'open' && group !== null

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div
          className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5"
          role="group"
          aria-label="Show"
        >
          {(['open', 'done'] as const).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              onClick={() => setView(v)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition-colors',
                view === v ? 'bg-brand-primary text-white' : 'text-gray-500 hover:text-brand-dark',
              )}
            >
              {v === 'open' ? 'Open' : 'Completed'}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3">
          {isAdmin && (
            <>
              {run.data && (
                <span className="text-xs text-gray-500">
                  Scan created {run.data.created} task(s).
                </span>
              )}
              <button
                type="button"
                onClick={() => run.mutate()}
                disabled={run.isPending}
                title="Checks every deal for a missed next-contact date or a stalled stage now, instead of waiting for the next automatic run, and raises the follow-up tasks they need."
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-brand-dark hover:border-brand-primary/40 disabled:opacity-60"
              >
                <Zap size={14} />
                {run.isPending ? 'Scanning…' : 'Run follow-up check'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark"
          >
            <Plus size={15} /> New task
          </button>
        </div>
      </div>
      {adding && <NewTaskModal onClose={() => setAdding(false)} />}
      {mine.length > 5 && (
        <IndexToolbar query={query} onQueryChange={setQuery} placeholder="Search tasks by what, deal or client…" shown={tasks.length} total={mine.length} />
      )}

      {isLoading && <p className="text-gray-400 text-sm">Loading…</p>}

      {narrowed && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          Showing only <span className="font-medium text-brand-darkest">{GROUP_TITLE[group]}</span>
          {onShowAll && (
            <button type="button" onClick={onShowAll} className="text-brand-primary hover:underline">
              Show all tasks
            </button>
          )}
        </div>
      )}
      {!isLoading && narrowed && tasks.length > 0 && groups.size === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400 text-sm">
          Nothing {group === 'week' ? 'due in the next 7 days' : group === 'today' ? 'due today' : 'here'}.
        </div>
      )}

      {!isLoading && tasks.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400 text-sm">
          {query ? `No tasks match “${query}”.` : view === 'open' ? 'No open tasks.' : 'No completed tasks yet.'}
        </div>
      )}

      {view === 'open'
        ? GROUP_ORDER.filter((g) => groups.has(g)).map((g) => (
            <Card
              key={g}
              title={
                <span id={`tasks-${g}`} className={g === 'overdue' ? 'text-red-700' : undefined}>
                  {GROUP_TITLE[g]} · {groups.get(g)!.length}
                </span>
              }
              bodyClassName="divide-y divide-gray-100"
            >
              {groups.get(g)!.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  overdue={g === 'overdue'}
                  onComplete={() => complete.mutate(t.id)}
                />
              ))}
            </Card>
          ))
        : tasks.length > 0 && (
            <Card bodyClassName="divide-y divide-gray-100">
              {tasks.map((t) => (
                <TaskRow key={t.id} task={t} />
              ))}
            </Card>
          )}
    </div>
  )
}

/** One task as a row: tick to complete, what and who it's about, when it's due. */
export function TaskRow({
  task: t,
  overdue = false,
  onComplete,
  hideContact = false,
}: {
  task: Task
  overdue?: boolean
  onComplete?: () => void
  /** On the contact's own card, naming them again is noise. */
  hideContact?: boolean
}) {
  const contactName = hideContact ? null : t.contactName
  return (
    <div className="px-4 py-3 flex items-start gap-3">
      {onComplete ? (
        <button
          type="button"
          onClick={onComplete}
          title="Mark done"
          aria-label={`Mark "${t.title}" done`}
          className="mt-0.5 shrink-0 w-5 h-5 rounded-full border border-gray-300 text-transparent hover:border-success hover:text-success flex items-center justify-center"
        >
          <Check size={12} />
        </button>
      ) : (
        <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-success/15 text-success flex items-center justify-center">
          <Check size={12} />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-brand-dark">{t.title}</div>
        {(t.dealTitle || contactName) && (
          <div className="text-xs mt-0.5 truncate text-gray-500">
            {t.dealId && t.dealTitle ? (
              // a new tab, not a navigation: working a list means opening several deals in turn
              <a
                href={`/deals/${t.dealId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary hover:underline inline-flex items-center gap-1"
                title="Open the deal in a new tab"
              >
                {t.dealTitle}
                <ExternalLink size={10} className="shrink-0" />
              </a>
            ) : (
              t.dealTitle
            )}
            {contactName && (
              <span>
                {t.dealTitle ? ' · ' : ''}
                {!t.dealId && t.contactId ? (
                  // a task about a person (no deal) links to them instead
                  <a href={`/contacts/${t.contactId}`} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline">
                    {contactName}
                  </a>
                ) : (
                  contactName
                )}
              </span>
            )}
          </div>
        )}
        {t.description && <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>}
      </div>
      <div className="shrink-0 text-right">
        {t.completedAt ? (
          <span className="text-xs text-gray-400">
            Done {formatShortDate(t.completedAt)}
            {t.dueAt && <span className="block">due {formatTaskDue(t.dueAt)}</span>}
          </span>
        ) : t.dueAt ? (
          <span className={cn('text-xs', overdue ? 'text-red-600 font-medium' : 'text-gray-500')}>
            {formatTaskDue(t.dueAt)}
          </span>
        ) : null}
        {t.source === 'automation' && (
          <div className="mt-1 text-[10px] uppercase tracking-wide text-brand-accent">auto</div>
        )}
      </div>
    </div>
  )
}
